/**
 * Photo ID (IdReview — vision; IdExtractor — OCR fallback). Two-layer payload:
 * `extract` (IdDocumentExtract) + the vision review beside it, plus
 * `additional_ids[]` with the same shape (EXTRACT_VIEWER.md §6.1).
 */
import { nowFields, parseDateLike, yearsBetween } from "../format/dates";

const nameFlagged = (p) => /name mismatch/i.test(String(p?.reviewNotes || ""));

/** Field specs for one ID, paths relative to `base` ("" or "additional_ids[0]."). */
const idFields = (base) => ({
  borrower: [
    { path: `${base}extract.firstName`, label: "First name" },
    { path: `${base}extract.middleName`, label: "Middle name" },
    {
      path: `${base}extract.lastName`,
      label: "Last name",
      note: (v, p) => (base === "" && nameFlagged(p) ? { text: "Doesn't match borrower on file", tone: "crit" } : undefined),
    },
    {
      path: `${base}extract.dateOfBirth`,
      label: "Date of birth",
      note: (v, p, d, now) => {
        const dob = parseDateLike(String(v || ""));
        return dob ? { text: `Age ${yearsBetween(dob, nowFields(now))}`, tone: "muted" } : undefined;
      },
    },
  ],
  document: [
    { path: `${base}extract.borrowerIdType`, label: "Type" },
    { path: `${base}extract.idNumber`, label: "ID number", format: "id" },
    { path: `${base}extract.issuingState`, label: "Issuing state" },
    { path: `${base}extract.issuingCountry`, label: "Issuing country" },
    { path: `${base}extract.idIssueDate`, label: "Issued" },
    { path: `${base}extract.idExpirationDate`, label: "Expiration date" },
  ],
  address: [{ path: `${base}extract.primaryAddress`, label: "Address" }],
  residency: [
    { path: `${base}extract.citizenshipStatus`, label: "Citizenship status" },
    { path: `${base}extract.foreignNationalConditionFlag`, label: "Foreign national condition" },
  ],
  review: [
    { path: `${base}requires_re_upload`, label: "Re-upload required", hideWhenEmpty: true },
    { path: `${base}re_upload_reason`, label: "Re-upload reason", hideWhenEmpty: true },
    { path: `${base}image_quality_rating`, label: "Image quality", format: "enum", hideWhenEmpty: true },
    { path: `${base}legible`, label: "Legible", hideWhenEmpty: true },
    { path: `${base}photo_present`, label: "Photo present", hideWhenEmpty: true },
    { path: `${base}photo_obscured`, label: "Photo obscured", hideWhenEmpty: true },
    { path: `${base}is_us_document`, label: "US document", hideWhenEmpty: true },
  ],
});

const primary = idFields("");

const idReviewConfig = {
  id: "id-review",
  schemas: ["IdReview", "IdExtractor"],
  routes: ["id"],
  title: "Photo ID",
  sections: [
    {
      kind: "fields",
      id: "review",
      title: "Review",
      fields: [
        ...primary.review,
        { path: "extraction_confidence", label: "Confidence", format: "percent", hideWhenEmpty: true },
        { path: "id_count", label: "IDs in file", format: "number", hideWhenEmpty: true },
      ],
    },
    { kind: "fields", id: "borrower", title: "Borrower", fields: primary.borrower },
    { kind: "fields", id: "id-document", title: "ID document", fields: primary.document },
    { kind: "fields", id: "address", title: "Address", fields: primary.address },
    { kind: "fields", id: "residency", title: "Residency", fields: primary.residency },
    { kind: "prose", id: "review-notes", title: "Review notes", path: "reviewNotes" },
    {
      kind: "custom",
      id: "additional-ids",
      title: "Additional IDs",
      consumes: ["additional_ids"],
      build: (p, h) =>
        (Array.isArray(p?.additional_ids) ? p.additional_ids : []).map((item, i) => {
          const f = idFields(`additional_ids[${i}].`);
          return h.group(
            `additional_ids[${i}]`,
            item?.extract?.borrowerIdType || `ID ${i + 2}`,
            [...f.review, ...f.borrower, ...f.document, ...f.address, ...f.residency],
          );
        }),
    },
  ],
  rules: [
    {
      id: "id-image-quality",
      tone: "warn",
      when: { path: "image_quality_rating", op: "in", value: ["poor", "unreadable"] },
      title: "Image quality is {image_quality_rating}",
      target: { sectionId: "review", path: "image_quality_rating" },
    },
  ],
};

export default idReviewConfig;
